import React, { useState, useEffect } from 'react';
import subscriptionService from '../../services/subscription-service';
import './AdminSubscriptionDashboard.scss';

const AdminSubscriptionDashboard = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [products, setProducts] = useState({});

  useEffect(() => {
    loadSubscriptions();
    loadProducts();
  }, []);

  const loadSubscriptions = async () => {
    try {
      const data = await subscriptionService.getAllSubscriptions();
      setSubscriptions(data);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
    }
    setLoading(false);
  };

  const loadProducts = async () => {
    try {
      const productService = await import('../../db/product-service');
      const allProducts = await productService.default.getAllProducts();
      const productMap = {};
      allProducts.forEach(p => {
        productMap[p._id] = p;
      });
      setProducts(productMap);
    } catch (error) {
      console.error('Error loading products:', error);
    }
  };

  const getDeliverySchedule = (subscription) => {
    if (!subscription.deliverySchedule || Object.keys(subscription.deliverySchedule).length === 0) {
      return [];
    }
    
    const schedule = [];
    Object.entries(subscription.deliverySchedule).forEach(([dateKey, day]) => {
      const deliveryDate = new Date(dateKey);
      schedule.push({
        date: dateKey,
        displayDate: deliveryDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
        status: day.status || 'pending',
        shot: day.shot,
        smoothie: day.smoothie
      });
    });
    
    return schedule.sort((a, b) => new Date(a.date) - new Date(b.date));
  };

  const getProductName = (productId) => {
    return products[productId]?.name || 'Product';
  };

  const getScheduleStats = (schedule) => {
    const delivered = schedule.filter(d => d.status === 'delivered').length;
    const dispatched = schedule.filter(d => d.status === 'dispatched').length;
    const pending = schedule.filter(d => d.status === 'pending').length;
    return { delivered, dispatched, pending, total: schedule.length };
  };

  const updateSubscriptionStatus = async (subscriptionId, newStatus) => {
    try {
      await subscriptionService.updateSubscriptionStatus(subscriptionId, newStatus);
      await loadSubscriptions();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Error updating subscription status');
    }
  };

  const filteredSubscriptions = subscriptions.filter(sub => {
    if (filter === 'all') return true;
    return sub.status === filter;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#28a745';
      case 'paused': return '#ffc107';
      case 'cancelled': return '#dc3545';
      default: return '#6c757d';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  if (loading) return <div className="loading">Loading subscriptions...</div>;

  if (selectedSubscription) {
    const schedule = getDeliverySchedule(selectedSubscription);
    const stats = getScheduleStats(schedule);
    
    return (
      <div className="admin-subscription-dashboard">
        <button className="back-btn" onClick={() => setSelectedSubscription(null)}>← Back to Subscriptions</button>
        <div className="subscription-detail">
          <div className="subscription-header">
            <h2>{selectedSubscription.planName}</h2>
            <span className="status" style={{ backgroundColor: getStatusColor(selectedSubscription.status) }}>
              {selectedSubscription.status.toUpperCase()}
            </span>
          </div>

          <div className="subscription-info">
            <div className="info-item">
              <span className="label">Customer ID:</span>
              <span>{selectedSubscription.customerId}</span>
            </div>
            <div className="info-item">
              <span className="label">Monthly Price:</span>
              <span>₹{selectedSubscription.monthlyPrice}</span>
            </div>
            <div className="info-item">
              <span className="label">Start Date:</span>
              <span>{formatDate(selectedSubscription.startDate)}</span>
            </div>
            <div className="info-item">
              <span className="label">Next Billing:</span>
              <span>{formatDate(selectedSubscription.nextBillingDate)}</span>
            </div>
            <div className="info-item">
              <span className="label">Deliveries:</span>
              <span>{stats.delivered} delivered, {stats.dispatched} dispatched, {stats.pending} pending</span>
            </div>
          </div>

          {schedule.length === 0 ? (
            <div className="no-schedule">
              <p>No delivery schedule found for this subscription.</p>
            </div>
          ) : (
            <div className="delivery-schedule">
              <h3>Delivery Schedule</h3>
              <div className="schedule-grid">
                {schedule.map((delivery, index) => (
                  <div key={delivery.date} className={`delivery-item ${delivery.status}`}>
                    <div className="delivery-header">
                      <span className="delivery-number">Day {index + 1}</span>
                      <span className="delivery-date">{delivery.displayDate}</span>
                    </div>
                    <div className="delivery-status">
                      {delivery.status === 'delivered' && <span className="status-badge delivered">✓ Delivered</span>}
                      {delivery.status === 'dispatched' && <span className="status-badge dispatched">Dispatched</span>}
                      {delivery.status === 'pending' && <span className="status-badge pending">Pending</span>}
                    </div>
                    <div className="delivery-products">
                      {delivery.shot && (
                        <div className="product-item">
                          <span className="product-type">Shot:</span>
                          <span className="product-name">{getProductName(delivery.shot)}</span>
                        </div>
                      )}
                      {delivery.smoothie && (
                        <div className="product-item">
                          <span className="product-type">Smoothie:</span>
                          <span className="product-name">{getProductName(delivery.smoothie)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="admin-subscription-dashboard">
      <div className="header">
        <h2>Subscription Management</h2>
        <div className="stats">
          <div className="stat">
            <span className="count">{subscriptions.filter(s => s.status === 'active').length}</span>
            <span className="label">Active</span>
          </div>
          <div className="stat">
            <span className="count">{subscriptions.filter(s => s.status === 'paused').length}</span>
            <span className="label">Paused</span>
          </div>
          <div className="stat">
            <span className="count">{subscriptions.length}</span>
            <span className="label">Total</span>
          </div>
        </div>
      </div>

      <div className="filters">
        <button 
          className={filter === 'all' ? 'active' : ''}
          onClick={() => setFilter('all')}
        >
          All
        </button>
        <button 
          className={filter === 'active' ? 'active' : ''}
          onClick={() => setFilter('active')}
        >
          Active
        </button>
        <button 
          className={filter === 'paused' ? 'active' : ''}
          onClick={() => setFilter('paused')}
        >
          Paused
        </button>
        <button 
          className={filter === 'cancelled' ? 'active' : ''}
          onClick={() => setFilter('cancelled')}
        >
          Cancelled
        </button>
      </div>

      <div className="subscriptions-list">
        {filteredSubscriptions.map(subscription => (
          <div key={subscription._id} className="subscription-card" onClick={() => setSelectedSubscription(subscription)} style={{ cursor: 'pointer' }}>
            <div className="subscription-header">
              <div className="customer-info">
                <h4>Customer: {subscription.customerId}</h4>
                <span className="plan-name">{subscription.planName}</span>
              </div>
              <div className="status-price">
                <span 
                  className="status"
                  style={{ backgroundColor: getStatusColor(subscription.status) }}
                >
                  {subscription.status.toUpperCase()}
                </span>
                <span className="price">₹{subscription.monthlyPrice}/month</span>
              </div>
            </div>

            <div className="subscription-details">
              <div className="detail">
                <span className="label">Start Date:</span>
                <span>{formatDate(subscription.startDate)}</span>
              </div>
              <div className="detail">
                <span className="label">Next Billing:</span>
                <span>{formatDate(subscription.nextBillingDate)}</span>
              </div>
              <div className="detail">
                <span className="label">Deliveries:</span>
                <span>{subscription.deliveriesCompleted} / 20</span>
              </div>
            </div>

            <div className="subscription-actions">
              {subscription.status === 'active' && (
                <button 
                  className="pause-btn"
                  onClick={() => updateSubscriptionStatus(subscription._id, 'paused')}
                >
                  Pause
                </button>
              )}
              {subscription.status === 'paused' && (
                <button 
                  className="resume-btn"
                  onClick={() => updateSubscriptionStatus(subscription._id, 'active')}
                >
                  Resume
                </button>
              )}
              {subscription.status !== 'cancelled' && (
                <button 
                  className="cancel-btn"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to cancel this subscription?')) {
                      updateSubscriptionStatus(subscription._id, 'cancelled');
                    }
                  }}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {filteredSubscriptions.length === 0 && (
        <div className="no-subscriptions">
          No subscriptions found for the selected filter.
        </div>
      )}
    </div>
  );
};

export default AdminSubscriptionDashboard;